import axios, { type AxiosResponse } from 'axios';
import { uiAppName } from '@nova/core/src/ui-app-name';
import type { IClient, INormalizedResponse } from '@/interfaces/api';
import config from '@/config/config';
import { isObject } from 'class-validator';

export interface INovaResponse<TItem extends object> {
  action: string;
  count: number;
  data: TItem | TItem[];
  entity: string;
  page: number;
  pageCount: number;
  total: number;
}

export type ConstructorParams = {
  token: string | null;
};
export default class NovaClient implements IClient {
  readonly client;
  constructor({ token }: ConstructorParams) {
    this.client = axios.create({
      baseURL: config.neutron_url,
      headers: {
        Authorization: `Bearer ${token}`,
        ...NovaClient.getGlobalHeaders()
      },
      paramsSerializer: params =>
        Object.keys(params)
          .filter(key => typeof params[key] !== 'undefined')
          .map(key => {
            if (isObject(params[key])) {
              params[key] = JSON.stringify(params[key]);
            }
            if (Array.isArray(params[key])) {
              return params[key]
                .map((v: string | number | boolean) => `${key}[]=${encodeURIComponent(v)}`)
                .join('&');
            }
            return `${key}=${encodeURIComponent(params[key])}`;
          })
          .join('&')
    });
  }

  private static isEnvelope<TItem extends object>(
    body: INovaResponse<TItem> | TItem
  ): body is INovaResponse<TItem> {
    return (
      typeof body === 'object' &&
      body !== null &&
      'data' in body &&
      body.data !== undefined &&
      body.data !== null
    );
  }

  /** An empty or non-object body (e.g. an empty string from a 204) holds no items. */
  private static toItems<TItem extends object>(rawData: TItem | TItem[]): TItem[] {
    if (Array.isArray(rawData)) {
      return rawData;
    }
    return typeof rawData === 'object' && rawData !== null ? [rawData] : [];
  }

  public static getGlobalHeaders() {
    return {
      'X-UI-AppName': uiAppName.PHOTON,
      'X-UI-Version': import.meta.env.VITE_NOVA_VERSION
    };
  }

  normalizeResponse<TItem extends object>(response: AxiosResponse): INormalizedResponse<TItem> {
    // Endpoints answer either with a paged envelope or with the bare entity.
    const body: INovaResponse<TItem> | TItem = response.data;
    const rawData = NovaClient.isEnvelope(body) ? body.data : body;
    const data = NovaClient.toItems(rawData);
    return {
      count: NovaClient.isEnvelope(body) ? body.count : data.length,
      data,
      errors: [],
      statusCode: response.status
    };
  }

  async get<TItem extends object>(
    resource: string,
    params?: object
  ): Promise<INormalizedResponse<TItem>> {
    const response = await this.client.get(resource, { params });
    return this.normalizeResponse<TItem>(response);
  }

  async post<TItem extends object>(
    resource: string,
    params?: object
  ): Promise<INormalizedResponse<TItem>> {
    const response = await this.client.post(resource, params);
    return this.normalizeResponse<TItem>(response);
  }

  async patch<TItem extends object>(
    resource: string,
    params?: object
  ): Promise<INormalizedResponse<TItem>> {
    const response = await this.client.patch(resource, params);
    return this.normalizeResponse<TItem>(response);
  }
}
